import React, { useRef } from "react";
import { Box, Typography, Divider } from "@mui/material";
import Spinner from "../../../Component/Spinner/Spinner";
import TrendsView from "./components/TrendsView";
import DetailView from "./components/DetailView";
import WhatChangedSection from "./components/WhatChangedSection";
import OnboardingTour from "./components/OnboardingTour";
import { DEFAULT_TOPIC_ORDER, humanizeTopicKey, getTopicColor } from "./components/topicOrder";

// Matches the trend chart's own "Overall" line color (TrendChart.js draws it
// white-cased near-black, on top of every colored topic line) — kept in sync
// manually since Overall isn't a real topic key and has no color-map entry.
const OVERALL_COLOR = "#0b0b0b";

// First-time walkthrough steps — ids match the DOM anchors OnboardingTour.js
// looks up: TrendsView's card, TrendChart's legend block, WhatChangedSection's
// card, and TopicBucketBreakdown's card.
const TOUR_STEPS = [
  {
    id: "cm-tour-trend-chart",
    title: "See sentiment over time",
    body: "Each line tracks one topic's sentiment across your runs. Points with a ring mean something significant happened — click one to see exactly why.",
  },
  {
    id: "cm-tour-legend",
    title: "Focus on one topic at a time",
    body: "Click a topic to hide its line, or double-click to show only that one. Hover a topic — here or on the chart itself — to trace its full history.",
  },
  {
    id: "cm-tour-what-changed",
    title: "Catch up fast on what moved",
    body: "This table lists only the topics that shifted 0.5 points or more since your last run. Click a row to see exactly what changed and why.",
  },
  {
    id: "cm-tour-topic-breakdown",
    title: "Drill into any topic's detail",
    body: "Click a topic to see every mention behind its score. Combined topics like “Purchase Enablers vs Barriers” and “Customer Complaints vs Praises” expand further into their own two sub-scores.",
  },
];

function formatRunLabel(runDate, isLatest) {
  if (!runDate) return "Latest";
  const d = new Date(runDate);
  if (Number.isNaN(d.getTime())) return "Latest";
  const formatted = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return isLatest ? `${formatted} · Latest` : formatted;
}

/**
 * Embedded results section for ConversationsV2 — mounted in place of the old
 * GenericTable output, in the same spot, receiving everything as props (no
 * fetching of its own: no history list call, no run-detail call). Forms and
 * the History sidebar in ConversationsV2 are untouched; this only replaces
 * the tables below them.
 *
 * No more Trends/Detail tabs — both render on one page, top to bottom:
 * the multi-run trend chart first (sample data across all topics + an
 * Overall aggregate), then "What Changed Since Last Run" (trend-explanation
 * data, so it belongs with the trend chart rather than the single-run
 * Detail view below), then a clear separator, then the single-run Detail
 * view for whichever run is currently loaded (Detail is inherently
 * single-date, so it stays visually distinct from the multi-date trend
 * section above it).
 */
export default function ConversationsResultsV3({
  discussionTopics,
  dynamicTopicOrder,
  loading,
  runDate,
  dates,
  selectedDate,
  onDateChange,
  trend,
  headerActions,
}) {
  const isLatest = !dates?.length || selectedDate === dates[0]?.execution_start_time;

  // "What Changed Since Last Run" — Overall first, then every topic (fixed
  // or dynamic/per-customer) that has a qualifying trendExplanation entry.
  // Lives here rather than in DetailView since it's trend-comparison data,
  // not single-run detail.
  const topics = discussionTopics || {};
  const topicKeysInOrder = [
    ...DEFAULT_TOPIC_ORDER.filter((k) => topics[k]),
    ...(dynamicTopicOrder || []).filter((k) => topics[k]),
  ];
  const trendExplanation = topics.trendExplanation || {};
  const whatChangedRows = [
    ...(trendExplanation.overall ? [{ key: "overall", label: "Overall", color: OVERALL_COLOR, explanation: trendExplanation.overall }] : []),
    ...topicKeysInOrder
      .filter((k) => trendExplanation[k])
      .map((k) => ({ key: k, label: humanizeTopicKey(k), color: getTopicColor(k), explanation: trendExplanation[k] })),
  ];
  // dates is latest-first; the earliest run is the last entry (or the only
  // one, if there's just one run so far) — distinguishes "no previous run to
  // compare against" from "compared, and nothing qualified".
  const datesList = dates || [];
  const isEarliestRun = datesList.length <= 1 || datesList[datesList.length - 1]?.execution_start_time === selectedDate;

  const tourRef = useRef(null);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2 }}>
        {headerActions}
        <Typography
          onClick={() => tourRef.current?.start()}
          sx={{ fontSize: 12.5, fontWeight: 600, color: "#1976d2", cursor: "pointer", userSelect: "none", "&:hover": { textDecoration: "underline" } }}
        >
          Take a tour
        </Typography>
      </Box>

      <TrendsView trend={trend} />

      <WhatChangedSection rows={whatChangedRows} isEarliestRun={isEarliestRun} />

      <Divider sx={{ borderColor: "#e4e7ec", mt: 1 }} />

      <Box>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#1a2027" }}>Detail &middot; {formatRunLabel(runDate, isLatest)}</Typography>
        <Typography sx={{ fontSize: 12.5, color: "#667085", mt: 0.25 }}>
          Full item-level data for a single run. Use the Run picker above to see detail for any other date.
        </Typography>
      </Box>

      {loading && <Spinner />}
      {!loading && (
        <DetailView
          discussionTopics={discussionTopics}
          dynamicTopicOrder={dynamicTopicOrder}
          runLabel={formatRunLabel(runDate, isLatest)}
          dates={dates}
          selectedDate={selectedDate}
          onDateChange={onDateChange}
        />
      )}

      <OnboardingTour ref={tourRef} steps={TOUR_STEPS} />
    </Box>
  );
}
